import type { DispatchOrder, DispatchOrderStatus } from "@khazzin/api-client";
import { formatDateTime } from "@khazzin/i18n/dates";
import { EmptyState } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import type { Tone } from "@khazzin/ui/styles";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";

/**
 * Orders → Dispatches (ADJ-R01-12, EP07-S23's "My Dispatches"): the bulk
 * loads of the store's stock — a wholesale sale, a return to the shop — newest
 * first, with where each stands. EP07-S23's own list, which already holds a
 * store user to their own store; read only here, because approving, picking
 * and handing over a load are the warehouse's.
 */

export function portalDispatchesKey(storeId: string) {
	return ["portal", storeId, "dispatches"] as const;
}

export const DISPATCH_TONE: Record<DispatchOrderStatus, Tone> = {
	draft: "neutral",
	submitted: "info",
	approved: "info",
	allocated: "info",
	picking: "warning",
	staged: "warning",
	dispatched: "success",
	partially_dispatched: "warning",
	completed: "success",
	cancelled: "danger",
};

export function DispatchesTab({ storeId, timeZone }: { storeId: string; timeZone: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const page = useCursorPagination<DispatchOrder>(
		(cursor, limit, signal) =>
			api
				.listDispatchOrders({ storeId, limit, signal, ...(cursor === null ? {} : { cursor }) })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: portalDispatchesKey(storeId), enabled: storeId !== "" },
	);
	const columns: Column<DispatchOrder>[] = [
		{
			id: "number",
			headerKey: "portal.orders.dispatches.columns.number",
			card: "wide",
			mono: true,
			cell: (r) => r.number,
		},
		{
			id: "status",
			headerKey: "portal.orders.dispatches.columns.status",
			cell: (r) => (
				<StatusChip tone={DISPATCH_TONE[r.status]}>
					{t(`portal.orders.dispatches.status.${r.status}`)}
				</StatusChip>
			),
		},
		{
			id: "reason",
			headerKey: "portal.orders.dispatches.columns.reason",
			cell: (r) => t(`portal.orders.dispatches.reason.${r.reason}`),
		},
		{
			id: "recipient",
			headerKey: "portal.orders.dispatches.columns.recipient",
			cell: (r) => r.recipient.name,
		},
		{
			id: "units",
			headerKey: "portal.orders.dispatches.columns.units",
			align: "end",
			cell: (r) => t("portal.orders.dispatches.units", { boxes: r.boxQty, units: r.unitQty }),
		},
		{
			id: "created",
			headerKey: "portal.orders.dispatches.columns.created",
			cell: (r) => formatDateTime(r.createdAt, { locale, timeZone }),
		},
	];
	return (
		<DataTable<DispatchOrder>
			screenKey="portal.orders.dispatches"
			caption={t("portal.orders.tabs.dispatches")}
			t={t}
			columns={columns}
			rowKey={(r) => r.dispatchId}
			page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
			status={page.status}
			error={page.error}
			onRetry={page.refetch}
			collapse="cards"
			pagination={{
				showingFrom: page.showingFrom,
				showingTo: page.showingTo,
				hasNext: page.hasNext,
				hasPrevious: page.hasPrevious,
				onNext: page.next,
				onPrevious: page.previous,
			}}
			empty={
				<EmptyState
					title={t("portal.orders.dispatches.emptyTitle")}
					body={t("portal.orders.dispatches.emptyBody")}
				/>
			}
		/>
	);
}
